import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "utils/moment/momentLocale";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import {
  Form,
  Radio,
  Button,
  Checkbox,
  Input,
  Upload,
  message,
  Row,
  Col,
} from "antd";
const { TextArea } = Input;

// components
import CustomDate from "components/Forms/Fields/CustomDate";
import TextField from "components/Forms/Fields/TextField";
import CustomTime from "components/Forms/Fields/CustomTime";
import CustomSelect from "components/Forms/Fields/CustomSelect";
import PreviewMedia from "components/Forms/Fields/PreviewMedia";

// containers
import UploadMedia from "mobile/containers/UploadMedia";
import SelectFieldAxios from "containers/SelectFieldAxios";

// utils
import { normFile } from "utils/uploadHelper";
import {
  DISPENSARY_PROGRAM_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  RECURRING_OPTIONS,
} from "utils/helpers/helper.constant";
import {
  getStatusesList,
  getTypeList,
} from "utils/helpers/helper.generateData";
import {
  clean,
  getArrayIds,
  sortByKey,
  getObjId,
} from "utils/helpers/helper.general";
// helper for forms
import {
  getRecurringDate,
  setTimeRangeAfterChanges,
  setDateRangeAfterChangesForRepeaters,
  setDateRangeAfterChangesForMore,
} from "utils/forms/forms.utils";
// utils
import api from "utils/api";
import { useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

function CreateAbsence({
  absence,
  isNew,
  loading,
  // actions
  change,
  // default
  className,
}) {
  const childClassName = classNames("create-absence-wrapper", className);
  const { t, i18n } = useTranslation();

  const timeFormat = "HH:mm";
  const dateFormat = "DD.MM.YYYY";

  const now = moment(absence?.date_from) || moment();
  const dateTo = (!!absence?.date_to && moment(absence?.date_to)) || moment();
  const nowTime =
    (!!absence?.time_from && moment(absence?.time_from)) || moment();
  const nowTimeTo =
    (!!absence?.time_to && moment(absence?.time_to)) || moment();

  let initAbsence = {
    status: "pending",
    type: "medical",
    duration: "day",
    remark: (!!absence?.remark && absence?.remark) || "",
    date_from: now,
    time_from: (!!absence?.time_from && nowTime) || null,
    // for duration more than 1 day
    date_to: (!!absence?.date_to && dateTo) || null,
    time_to: (!!absence?.time_to && nowTimeTo) || null,
    // For  "Absence récurrente" part
    recurring: null,
    recurring_date: null,
    unjustified: absence?.type == "unjustified" || !!absence?.unjustified,
  };

  const [absenceInfo, setAbsenceInfo] = useState(absence || initAbsence);
  const [absenceType, setAbsenceType] = useState(
    absence?.type || initAbsence?.type,
  );
  const [absenceStatus, setAbsenceStatus] = useState(
    absence?.status || initAbsence?.status,
  );

  const [isDisabled, setIsDisabled] = useState(false);
  const [durationType, setDurationType] = useState(initAbsence?.duration);
  const [endponitObj, setEndponitObj] = useState(false);
  const [currentDay, setCurrentDay] = useState(now);
  const [currentTime, setCurrentTime] = useState(nowTime);

  const [currentStudent, currentStudentRef, setCurrentStudent] = useRefState(
    false,
  );
  // only for teacher mode
  const [studentParent, studentParentRef, setStudentParent] = useRefState(
    false,
  );

  // for medical, no-medical
  const recurringOption = useCallback(() => RECURRING_OPTIONS(), [i18n?.language]);
  const [recurringType, setRecurringType] = useState("every_week");
  const [isRepeate, setIsRepeate] = useState(false);
  const [hasRecur, setHasRecur] = useState(false);

  // for media
  const [mediaFiles, mediaFilesRef, setMediaFiles] = useRefState([]);
  const [uploading, setUploading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [AbsFormCreate] = Form.useForm();

  // Clearing the state after unmounting a component
  useEffect(() => {
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state for SelectFieldAxios component
      setEndponitObj(false);
      setCurrentStudent(false);
      setStudentParent(false);
      setAbsenceInfo(false);
    };
  }, []);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (!!absence) {
      const hasValFormData = clean(absence);
      setIsDisabled(false);
      setAbsenceInfo(absence);
      setAbsenceType(absence?.type);
      setAbsenceStatus(absence?.status);
      setDurationType(hasValFormData?.duration || initAbsence?.duration);

      AbsFormCreate.setFieldsValue({
        type: absence?.type,
        duration: absence?.duration || initAbsence?.duration,
        reason: absence?.reason || null,
        status: absence?.status || initAbsence?.status,
        student: absence?.student?._id || absence?.student,
        class: absence?.student?.class_info?.title,
      });

      AbsFormCreate.setFieldsValue({
        // for duration more than 1 day
        date_from:
          (!!hasValFormData?.date_from && moment(hasValFormData?.date_from)) ||
          null,
        time_from:
          (!!hasValFormData?.time_from && moment(hasValFormData?.time_from)) ||
          null,
        date_to:
          (!!hasValFormData?.date_to && moment(hasValFormData?.date_to)) ||
          null,
        time_to:
          (!!hasValFormData?.time_to && moment(hasValFormData?.time_to)) ||
          null,
      });

      if (
        hasValFormData?.type != "sickness" &&
        hasValFormData?.type != "unjustified"
      ) {
        AbsFormCreate.setFieldsValue({
          // For  "Absence récurrente" part
          recurring:
            (!!!hasValFormData?.origin && hasValFormData?.recurring) || null,
          recurring_date:
            (!!!hasValFormData?.origin &&
              !!hasValFormData?.recurring_date &&
              moment(hasValFormData?.recurring_date)) ||
            null,
        });
      }

      if (hasValFormData?.status == "sick") {
        AbsFormCreate.setFieldsValue({
          date_to: null,
          time_to: null,
        });
      }

      if (!!!hasValFormData?.origin && hasValFormData?.recurring) {
        setRecurringType(hasValFormData.recurring);
        setIsRepeate(true);
        setHasRecur(true);
      }

      if (hasValFormData?.date_from) {
        setCurrentDay(moment(hasValFormData.date_from));
      } else {
        setCurrentDay(now);
      }

      if (hasValFormData?.time_from) {
        setCurrentTime(moment(hasValFormData.time_from));
      } else {
        setCurrentTime(nowTime);
      }

      if (hasValFormData?.student?._id) {
        setCurrentStudent(hasValFormData?.student);
        const contact_person =
          hasValFormData?.student?.contact_person?._id ||
          hasValFormData?.student?.contact_person;
        const parent =
          contact_person ||
          hasValFormData?.student?.parents?.[0]?._id ||
          hasValFormData?.student?.parents?.[0];
        setStudentParent(parent);
      }

      if (hasValFormData?.media) {
        AbsFormCreate.setFieldsValue({
          media: hasValFormData?.media,
        });
        setMediaFiles(hasValFormData.media);
      }
    } else {
      setCurrentDay(now);
      setCurrentTime(nowTime);
    }
  }, [absence]);

  const getDaysWeek = useCallback(() => DAY_OF_WEEK_OPTIONS(), [i18n?.language]);

  /*
   * Check current date and day of the week
   * for generate Courses list
   */
  useEffect(() => {
    if (currentDay) {
      // 1 - Monday, 7 - Sunday
      const week = currentDay.isoWeekday();
      setEndponitObj({
        course_week: getDaysWeek()[week - 1]?.value,
      });
    }
  }, [currentDay]);

  useEffect(() => {
    if (currentDay && isRepeate && !hasRecur && !!!absenceInfo?.origin) {
      const newDate = getRecurringDate(recurringType, currentDay);
      AbsFormCreate.setFieldsValue({
        recurring_date: newDate,
      });
      setDateRangeAfterChangesForRepeaters(
        AbsFormCreate,
        AbsFormCreate.getFieldsValue(),
      );
    }
  }, [isRepeate, hasRecur, currentDay]);

  const resetInitFileds = useCallback(
    (durationVal, allval) => {
      let initFileds = {};
      const isSickness = allval?.type == "sickness";
      const isUnjustified = allval?.type == "unjustified";
      const isMedical = allval?.type == "medical";
      if (!!isSickness) {
        initFileds = {
          date_from:
            (!!allval?.date_from && moment(allval?.date_from, dateFormat)) ||
            moment(now, dateFormat),
          // for duration more than 1 day
          time_from:
            (!!allval?.time_from && moment(allval?.time_from, timeFormat)) ||
            moment(nowTime, timeFormat),
          date_to: null,
          time_to: null,
        };
      } else if (!!isUnjustified) {
        initFileds = {
          date_from:
            (!!allval?.date_from && moment(allval?.date_from, dateFormat)) ||
            moment(now, dateFormat),
          // for duration more than 1 day
          time_from:
            (!!allval?.time_from && moment(allval?.time_from, timeFormat)) ||
            moment(nowTime, timeFormat),
          date_to:
            (!!allval?.date_to && moment(allval?.date_to, dateFormat)) || null,
          time_to:
            (!!allval?.time_to && moment(allval?.time_to, timeFormat)) || null,
        };
      } else {
        switch (durationVal) {
          case "day": {
            initFileds = {
              date_from:
                (!!allval?.date_from &&
                  moment(allval?.date_from, dateFormat)) ||
                moment(now, dateFormat),
              // for duration more than 1 day
              time_from:
                (!!allval?.time_from &&
                  moment(allval?.time_from, timeFormat)) ||
                moment(nowTime, timeFormat),
              date_to: null,
              time_to:
                (!!allval?.time_to && moment(allval?.time_to, timeFormat)) ||
                moment(nowTimeTo, timeFormat),
            };
            break;
          }
          case "more": {
            initFileds = {
              // for duration more than 1 day
              date_from:
                (!!allval?.date_from &&
                  moment(allval?.date_from, dateFormat)) ||
                moment(now, dateFormat),
              time_from:
                (!!allval?.time_from &&
                  moment(allval?.time_from, timeFormat)) ||
                moment(nowTime, timeFormat),
              date_to: null,
              time_to:
                (!!allval?.time_to && moment(allval?.time_to, timeFormat)) ||
                moment(nowTimeTo, timeFormat),
            };
            break;
          }
        }
      }

      AbsFormCreate.setFieldsValue({
        // For  "Absence récurrente" part
        recurring: null,
        recurring_date: null,
        ...initFileds,
      });
      setHasRecur(false);
    },
    [now, AbsFormCreate],
  );

  // Main result/save data
  const onFinish = values => {
    if (values?.type) {
      setAbsenceType(values.type);
    }

    let newObj = { ...absenceInfo, ...values };

    if (values?.type == "sickness" && absenceInfo?.type !== "sickness") {
      newObj.status = "sick";
    }
    if (values?.type == "unjustified" && absenceInfo?.type !== "unjustified") {
      newObj.status = "unjustified";
    }

    newObj.parent = studentParentRef?.current || null;

    setAbsenceInfo(newObj);
    const cleanData = newObj;

    cleanData.media = mediaFiles;
    cleanData.status = absenceStatus || cleanData.status;

    // update DB
    change(cleanData);

    AbsFormCreate.resetFields();
  };

  // Select student for current absence
  let selectStudents = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: t("labels.student"),
      name: "student",
      endponit: `students/list/select`,
      endponitSend: {},
      required: true,
      value: "",
      disabled: !!absenceInfo?.origin,
      displayLabel: true,
      type: "axios-select",
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      onSelect: (LabeledValue, option) => {
        setCurrentStudent(option?.item);
        AbsFormCreate.setFieldValue("class", option.item.class_info.title);
        const contact_person =
          option?.item?.contact_person?._id || option?.item?.contact_person;
        const parent =
          contact_person ||
          option?.item?.parents[0]?._id ||
          option?.item?.parents[0];
        setStudentParent(parent);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <>
        <SelectFieldAxios
          isSearch={true}
          sortBy="label"
          className="axios-select"
          placeholder={t("inputs.selectStudent")}
          tagRender={newElementSelect.tagRender}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
          onSelect={newElementSelect.onSelect}
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          disabled={!!absenceInfo?.origin}
        />
        {studentParentRef?.current && (
          <Form.Item
            name="parent"
            hidden={true}
            initialValue={studentParentRef.current}
            rules={[
              {
                required: false,
                message: "",
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  }, [
    studentParentRef,
    setStudentParent,
    studentParentRef,
    setCurrentStudent,
    absenceInfo?.origin,
    i18n?.language,
  ]);

  const getDateOptionMedicalRepeatable = useCallback(
    currentDay => {
      return (
        !!!absenceInfo?.origin && (
          <>
            {!isRepeate && (
              <div
                className="is-repeate-action"
                onClick={() => {
                  setIsRepeate(true);
                  const newDate = getRecurringDate(recurringType, currentDay);
                  AbsFormCreate.setFieldsValue({
                    recurring: recurringType,
                    recurring_date: newDate,
                  });
                  setAbsenceInfo(prev => ({
                    ...prev,
                    recurring: recurringType,
                    recurring_date: newDate,
                  }));
                  setDateRangeAfterChangesForRepeaters(
                    AbsFormCreate,
                    AbsFormCreate.getFieldsValue(),
                  );
                  setHasRecur(false);
                }}
              >
                {t("absences.recurrentAbsenceQuestion")}
              </div>
            )}

            {isRepeate && (
              <div className="is-repeate-section">
                <div className="is-repeate-header">
                  <h4 className="title">{t("absences.recurrentAbsence")}</h4>
                  <div
                    className="is-repeate-hide"
                    onClick={() => {
                      setIsRepeate(false);
                      AbsFormCreate.setFieldsValue({
                        recurring: null,
                        recurring_date: null,
                      });
                      setAbsenceInfo(prev => ({
                        ...prev,
                        recurring: null,
                        recurring_date: null,
                      }));
                      setHasRecur(false);
                    }}
                  >
                    {t("absences.hide")}
                  </div>
                </div>
                <Row gutter={(15, 15)}>
                  <Col sm={24} md={24} lg={12} xl={12}>
                    <CustomSelect
                      label={t("absences.reccurence")}
                      placeholder={t("inputs.select")}
                      name={`recurring`}
                      options={recurringOption}
                      onSelect={(LabeledValue, option) => {
                        setRecurringType(LabeledValue);
                        const newDateSet = getRecurringDate(
                          LabeledValue,
                          currentDay,
                        );
                        setAbsenceInfo(prev => ({
                          ...prev,
                          recurring: LabeledValue,
                          recurring_date: newDateSet,
                        }));
                        AbsFormCreate.setFieldsValue({
                          recurring: LabeledValue,
                          recurring_date: newDateSet,
                        });
                        setDateRangeAfterChangesForRepeaters(
                          AbsFormCreate,
                          AbsFormCreate.getFieldsValue(),
                        );
                        setHasRecur(false);
                      }}
                    />
                  </Col>
                  <Col sm={24} md={24} lg={12} xl={12}>
                    <CustomDate
                      label={t("inputs.endDate")}
                      name="recurring_date"
                      // disabledDate={disabledDateFrom}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </>
        )
      );
    },
    [
      AbsFormCreate,
      recurringOption,
      isRepeate,
      recurringType,
      setHasRecur,
      absenceInfo?.origin,
    ],
  );

  const getDateOptionMedical = useMemo(() => {
    return (
      <>
        {durationType == "more" ? (
          <>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.from")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                  value={currentDay}
                  onChange={val => {
                    setCurrentDay(val);
                    setHasRecur(false);
                  }}
                />
                <CustomTime
                  name="time_from"
                  value={currentTime}
                  onChange={val => {
                    setCurrentTime(val);
                  }}
                />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.to")}</h5>
              <div className="form-row__fileds">
                <CustomDate name="date_to" />
                <CustomTime name="time_to" />
              </div>
            </div>
          </>
        ) : (
          <div className="form-row">
            <h5 className="form-row__label">{t("common.from")}</h5>
            <div className="form-row__fileds">
              <CustomDate
                name="date_from"
                value={currentDay}
                onChange={val => {
                  setCurrentDay(val);
                  setHasRecur(false);
                }}
              />
              <CustomTime
                name="time_from"
                label="inputs.startTime"
                value={currentTime}
                onChange={val => {
                  setCurrentTime(val);
                }}
              />
              <CustomTime name="time_to" label="inputs.endTime" />
            </div>
          </div>
        )}

        {durationType != "more" && getDateOptionMedicalRepeatable(currentDay)}
      </>
    );
  }, [
    AbsFormCreate,
    durationType,
    endponitObj,
    currentStudent,
    currentTime,
    currentDay,
    isRepeate,
    absenceInfo?.origin,
    i18n?.language,
  ]);

  const uploadMediaFiles = useCallback(data => setMediaFiles(data), []);
  const fileOnRemove = async file => {
    setRemoveLoading(true);
    try {
      if (typeof file === "string") {
        await api.media.delete(file);
      } else if (absenceInfo && absenceInfo?.media?.length > 0) {
        absenceInfo?.media?.map(async fileUrl => {
          if (typeof fileUrl === "string") {
            await api.media.delete(fileUrl);
          }
        });
      }
      setRemoveLoading(false);
    } catch (err) {
      setRemoveLoading(false);
      console.error(err);
    }

    AbsFormCreate.setFieldsValue({
      media: [],
    });
    setMediaFiles([]);
    setAbsenceInfo(prev => ({ ...prev, media: [] }));
  };
  const customItemRender = useCallback(
    (originNode, file) => {
      setUploading(false);
      const initFile = mediaFiles?.length > 0 ? mediaFiles[0] : file;
      return (
        <PreviewMedia showTitle={false} file={initFile} remove={fileOnRemove} />
      );
    },
    [mediaFiles, fileOnRemove],
  );

  const getMediaField = useMemo(() => {
    return (
      <div className="absence-media-upload">
        <h3 className="media-title">{t("absences.proof")}</h3>
        {mediaFiles && mediaFiles?.length == 0 && (
          <div className="proof-option-upload">
            <UploadMedia
              name="media"
              media={mediaFiles}
              loading={uploading}
              setProgress={setUploading}
              uploadChange={uploadMediaFiles}
            />
          </div>
        )}
        {mediaFiles && mediaFiles?.length > 0 && (
          <div className="form-proof">
            <Form.Item
              name="media"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              noStyle
              normalize={(value, prevValue, prevValues) => {
                if (value.length > 1) {
                  message.error(t("absences.downloadOneFile"));
                  return false;
                }
              }}
            >
              <Upload
                multiple={false}
                maxCount={1}
                fileList={mediaFiles}
                onRemove={fileOnRemove}
                itemRender={customItemRender}
              />
            </Form.Item>
          </div>
        )}
      </div>
    );
  }, [
    mediaFiles,
    fileOnRemove,
    setUploading,
    uploading,
    removeLoading,
    customItemRender,
    uploadMediaFiles,
    i18n?.language,
  ]);

  const getReason = useCallback(
    type => {
      const sortBy = "label";
      const DEFAULT_REASONS =
        type == "medical" ? DISPENSARY_PROGRAM_OPTIONS() : ACTIVITY_REASON_OPTIONS();
      let listReasons = DEFAULT_REASONS?.map(elem => {
        return {
          key: elem?.key,
          label: elem?.label,
          title: elem?.title || elem?.label,
          value: elem?.value,
        };
      });
      // sort options
      listReasons.sort(sortByKey(sortBy));
      return listReasons;
    },
    [i18n?.language],
  );
  
  const getStatuses = useCallback(absenceType => getStatusesList(absenceType), [i18n?.language]);

  const absFromValuesChange = useCallback(
    (values, allValues) => {
      if (values?.status) {
        setAbsenceStatus(values?.status);
      }
      if (
        values?.duration &&
        allValues?.type != "sickness" &&
        allValues?.type != "unjustified"
      ) {
        setDurationType(values?.duration);
        setIsRepeate(false);
        resetInitFileds(values?.duration, allValues);
      }
      if (values?.type) {
        setAbsenceType(values?.type);
        setIsRepeate(false);
        resetInitFileds(initAbsence?.duration, allValues);

        if (values?.type == "sickness") {
          setAbsenceStatus("sick");
          setDurationType(false);
          AbsFormCreate.setFieldsValue({
            status: "sick",
            duration: null,
            time_from:
              (!!allValues?.time_from &&
                moment(allValues?.time_from, timeFormat)) ||
              moment(nowTime, timeFormat),
            time_to: null,
          });
        } else if (values?.type == "unjustified") {
          setAbsenceStatus("unjustified");
          setDurationType(false);
          AbsFormCreate.setFieldsValue({
            status: "unjustified",
            duration: null,
            time_from:
              (!!allValues?.time_from &&
                moment(allValues?.time_from, timeFormat)) ||
              moment(nowTime, timeFormat),
            time_to: null,
          });
        } else {
          !values?.duration && setDurationType("day");
          setAbsenceStatus("pending");
          AbsFormCreate.setFieldsValue({
            status: "pending",
            duration: "day",
          });
        }
      }

      setTimeRangeAfterChanges(AbsFormCreate, allValues);
      setDateRangeAfterChangesForRepeaters(AbsFormCreate, allValues);
      setDateRangeAfterChangesForMore(AbsFormCreate, allValues);

      setIsDisabled(false);
    },
    [initAbsence, AbsFormCreate],
  );

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormCreate}
        className="form-absence__form"
        name="form_absence_create"
        initialValues={initAbsence}
        onFinish={onFinish}
        onValuesChange={absFromValuesChange}
        disabled={!!loading}
      >
        <Row gutter={(15, 15)} style={{ marginBottom: "30px" }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            {selectStudents}
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <TextField
              data={{
                name: "class",
                label: t("labels.class"),
                disabled: true,
              }}
            />
          </Col>
          <Col sm={24} md={24} lg={24} xl={24} style={{ marginTop: "15px" }}>
            <CustomSelect
              // className="accent-label"
              label={t("absences.absencesType")}
              placeholder={t("inputs.select")}
              name={`type`}
              options={getTypeList()}
              disabled={!!absenceInfo?.origin}
            />
          </Col>
          {
            <Col sm={24} style={{ marginTop: "15px" }}>
              <Form.Item
                name="unjustified"
                key="unjustified"
                className="forms-field forms-field--checkbox"
                valuePropName="checked"
              >
                <Checkbox
                  disabled={absenceType == "unjustified"}
                  checked={
                    absenceType == "unjustified" || !!absenceInfo?.unjustified
                  }
                >
                  <span className="checkbox-label">
                    {t("absences.unjustifiedAbsence")}
                  </span>
                </Checkbox>
              </Form.Item>
            </Col>
          }
          {absenceType == "no-medical" && (
            <Col sm={24} md={24} lg={24} xl={24} style={{ marginTop: "15px" }}>
              <CustomSelect
                //className="accent-label"
                label={`${t("absences.reason")} : ${
                  absenceType == "medical"
                    ? t("global.absenceTypes.medical")
                    : t("global.absenceTypes.no-medical")
                }`}
                placeholder={t("inputs.select")}
                name={`reason`}
                options={getReason(absenceType)}
              />
            </Col>
          )}
        </Row>

        {absenceType != "sickness" && absenceType != "unjustified" && (
          <Form.Item name="duration" label={t("absences.duration")}>
            <Radio.Group
              className="radio-group radio-group--horizontal"
              options={[
                {
                  key: "day",
                  value: "day",
                  label: (
                    <>
                      <span className="label__title">
                        {t("absences.durationDayLabel")}
                      </span>
                    </>
                  ),
                },
                {
                  key: "more",
                  value: "more",
                  label: (
                    <>
                      <span className="label__title">
                        {t("absences.durationMoreLabel")}
                      </span>
                    </>
                  ),
                },
              ]}
              optionType="Radio"
              disabled={!!absenceInfo?.origin}
            />
          </Form.Item>
        )}

        {absenceType == "sickness" || absenceType == "unjustified" ? (
          <>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.from")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                  value={currentDay}
                  onChange={val => {
                    setCurrentDay(val);
                  }}
                />
                <CustomTime
                  name="time_from"
                  label="inputs.startTime"
                  value={currentTime}
                  onChange={val => {
                    setCurrentTime(val);
                  }}
                />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.to")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_to"
                  required={false}
                  loading={loading}
                  disabled={absenceStatus == "sick"}
                />
                <CustomTime
                  name="time_to"
                  label="inputs.endTime"
                  required={false}
                  loading={loading}
                  disabled={absenceStatus == "sick"}
                />
              </div>
            </div>
          </>
        ) : (
          getDateOptionMedical
        )}

        {getMediaField}

        <div className="form-row remark-textarea-row">
          <h5 className="form-row__label">{t("common.note")}</h5>
          <div className="form-row__fileds">
            <Form.Item
              name="remark"
              className="remark-textarea"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <TextArea
                disabled={false}
                row="5"
                placeholder={t("common.note")}
              />
            </Form.Item>
          </div>
        </div>

        <CustomSelect
          className={`accent-label select-status ${absenceStatus}`}
          label={t("common.status")}
          valuse={absenceStatus}
          placeholder={t("inputs.select")}
          name={`status`}
          options={getStatuses(absenceType)}
        />

        <div className="absence-actions-wrapper" style={{ marginTop: "50px" }}>
          <Button
            className="next-btn"
            form="form_absence_create"
            htmlType="submit"
            disabled={isDisabled}
            style={{ marginLeft: 0 }}
            loading={loading || uploading || removeLoading}
          >
            {t("common.save")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

CreateAbsence.defaultProps = {
  loading: false
};

CreateAbsence.propTypes = {
  isNew: PropTypes.bool,
  absence: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  change: PropTypes.func.isRequired,
};

export default CreateAbsence;
